"use client";

import type { HTMLAttributes, ReactNode } from "react";
import { WEB_PAGE_SURFACE_MD_CLASS } from "@/components/layout/WebPageSurface";
import { PageLayout } from "@/components/layout";
import { cn } from "@/lib/utils";

export const AUTH_WEB_CONTENT_SURFACE_CLASS = `${WEB_PAGE_SURFACE_MD_CLASS} lg:px-8 lg:py-8`;

export const AUTH_WEB_ASIDE_CARD_CLASS =
  "rounded-[28px] border border-[#F3E7D0] bg-white/90 px-6 py-6 shadow-[0_16px_40px_rgba(16,24,40,0.08)]";

interface AuthOnboardingShellProps {
  children: ReactNode;
  aside?: ReactNode;
  className?: string;
  contentClassName?: string;
  asideClassName?: string;
}

interface AuthOnboardingContentSurfaceProps extends HTMLAttributes<HTMLDivElement> {
  testId?: string;
}

interface AuthOnboardingAsideCardProps {
  title: string;
  children?: ReactNode;
  description?: string;
  eyebrow?: string;
  className?: string;
  testId?: string;
}

export function AuthOnboardingShell({
  children,
  aside,
  className,
  contentClassName,
  asideClassName,
}: AuthOnboardingShellProps) {
  return (
    <PageLayout responsive={true}>
      <main
        data-testid="auth-onboarding-shell"
        className={cn(
          "mx-auto flex min-h-dvh w-full max-w-[1180px] flex-col gap-6 px-4 py-5 md:px-8 md:py-8 lg:py-10",
          aside
            ? "lg:grid lg:grid-cols-[minmax(0,1fr)_360px] lg:gap-8"
            : "lg:flex lg:items-center",
          className,
        )}
      >
        <section
          className={cn(
            aside ? "min-w-0" : "mx-auto w-full max-w-[720px]",
            contentClassName,
          )}
        >
          {children}
        </section>
        {aside ? (
          <aside
            data-testid="auth-onboarding-aside"
            className={cn("hidden lg:block lg:min-w-0", asideClassName)}
          >
            {aside}
          </aside>
        ) : null}
      </main>
    </PageLayout>
  );
}

export function AuthOnboardingContentSurface({
  children,
  className,
  testId,
  ...props
}: AuthOnboardingContentSurfaceProps) {
  return (
    <div
      data-testid={testId}
      {...props}
      className={cn(
        "relative z-10 flex min-h-dvh flex-col px-4 py-5 lg:min-h-[720px] lg:px-0 lg:py-0",
        AUTH_WEB_CONTENT_SURFACE_CLASS,
        className,
      )}
    >
      {children}
    </div>
  );
}

export function AuthOnboardingAsideCard({
  title,
  children,
  description,
  eyebrow,
  className,
  testId,
}: AuthOnboardingAsideCardProps) {
  return (
    <section
      data-testid={testId}
      className={cn("space-y-3", AUTH_WEB_ASIDE_CARD_CLASS, className)}
    >
      {eyebrow ? (
        <p className="text-xs font-semibold uppercase tracking-[0.14em] text-[#667085]">
          {eyebrow}
        </p>
      ) : null}
      <div className="space-y-2">
        <h2 className="text-xl font-semibold tracking-tight text-[#344054]">
          {title}
        </h2>
        {description ? (
          <p className="text-sm leading-6 text-[#667085]">{description}</p>
        ) : null}
      </div>
      {children}
    </section>
  );
}
